import React, { useState } from 'react';
import { RefreshCw, Settings as SettingsIcon, Database, ShoppingBag, DollarSign } from 'lucide-react';
import { productsAPI, ordersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import api from '../../services/api';

export function Settings() {
  const [syncing, setSyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const handleFullSync = async () => {
    try {
      setSyncing(true);
      toast.info('Starting full sync...');

      // Step 1: Sync products (gets latest costs from Shopify)
      toast.info('Syncing products...');
      const productsResponse = await productsAPI.sync();
      toast.success(`Synced ${productsResponse.data.synced} products with latest costs`);

      // Step 2: Sync orders (uses updated costs)
      toast.info('Syncing orders...');
      const ordersResponse = await ordersAPI.sync({ force_full: true });
      toast.success(`Synced ${ordersResponse.data.synced} order items`);

      toast.success('Full sync completed!');
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleBackfillCosts = async () => {
    try {
      setBackfilling(true);
      toast.info('Backfilling historical costs...');

      const response = await api.post('/orders/backfill-costs');

      toast.success(`Updated ${response.data.updated} historical orders with correct costs!`);
    } catch (error) {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Settings</h1>

      {/* Shopify Connection */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <ShoppingBag className="text-green-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Shopify Connection</h2>
        </div>
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
          <p className="text-green-800 font-medium">✓ Connected to Shopify</p>
          <p className="text-green-700 text-sm mt-1">
            Store: {process.env.REACT_APP_SHOPIFY_STORE || 'ibexwear.myshopify.com'}
          </p>
        </div>
      </div>

      {/* Sync Settings */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <RefreshCw className="text-blue-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Synchronization</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Syncs all products with latest costs from Shopify, then syncs new orders.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ First run fetches all orders. After that, only new orders are fetched.
        </p>
        <button
          onClick={handleFullSync}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Run Full Sync'}
        </button>
        <p className="text-gray-500 text-sm mt-3">
          ℹ️ Automatic sync runs every 10 minutes in the background
        </p>
      </div>

      {/* Backfill Historical Costs */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <DollarSign className="text-orange-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Backfill Historical Costs</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Updates cost price and net profit for all existing orders using the latest costs from your products.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ Run this once after your first full sync to fix profit data on all historical orders.
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-4">
          <p className="text-yellow-800 text-sm font-medium">
            ⚠️ Make sure to run "Run Full Sync" first so product costs are up to date before backfilling.
          </p>
        </div>
        <button
          onClick={handleBackfillCosts}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50"
        >
          <DollarSign className={`mr-2 ${backfilling ? 'animate-spin' : ''}`} size={20} />
          {backfilling ? 'Backfilling...' : 'Backfill Historical Costs'}
        </button>
      </div>

      {/* Database Info */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center mb-4">
          <Database className="text-purple-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Database</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Database Status</span>
            <span className="font-semibold text-green-600">Connected</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Last Sync</span>
            <span className="font-semibold text-gray-800">
              {new Date().toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;